import { Component, HostListener, OnInit, ViewContainerRef, inject, signal, viewChild, viewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';

import { NgSelectModule } from '@ng-select/ng-select';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { RegisterComponent } from '../../../components/component-lookup';
import { CanDeactivateComponent } from '../../../../shared/guards/pending-changes.guard';
import { GlobalConstants } from '../../../../shared/global-constants';
import { InstitutionAutocompleteComponent } from '../../../components/institution-autocomplete/institution-autocomplete.component';
import { InstitutionProfile, InstitutionProfileService, SimilarInstitutionsRequest, TargetedInstitutionsRequest } from '../../../services/ncua/institution-profile.service';
import { CreateGroup, Group } from '../../../services/group.service';
import { DialogService } from '../../../../shared/services/dialog.service';
import { StateService } from '../../../services/state.service';
import { UserService } from '../../../../shared/services/user.service';
import { Institution } from '../../../services/institution.service';
import { DateSelectorComponent } from '../../../components/date-selector/date-selector.component';
import { CreateGroupDialogComponent, CreateGroupDialogResults } from '../../create-group-dialog/create-group-dialog.component';
import { ReferenceService, TypeOfMembership } from '../../../services/reference.service';

enum PeerGroupType {
  None,
  Similar,
  Targeted,
  Distinct
}

@RegisterComponent('NcuaCreateGroup')
@Component({
  selector: 'app-ncua-create-group',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxMaskDirective, NgSelectModule, NgxPaginationModule,
    InstitutionAutocompleteComponent, DateSelectorComponent],
  templateUrl: './create-group.component.html',
  styleUrl: './create-group.component.scss',
  providers: [provideNgxMask({ thousandSeparator: ',' })]
})
export class NcuaCreateGroupComponent implements OnInit, CanDeactivateComponent {
  institutionAutocompletes = viewChildren(InstitutionAutocompleteComponent);
  dialog = viewChild.required('dialog', { read: ViewContainerRef });

  private router = inject(Router);
  private dialogService = inject(DialogService);
  private profileService = inject(InstitutionProfileService);
  private stateService = inject(StateService);
  private referenceService = inject(ReferenceService);
  readonly groups = inject(StateService).groups;
  readonly institution = inject(StateService).institution;
  readonly user = inject(UserService).state;

  readonly PeerGroupType = PeerGroupType;
  selection: PeerGroupType = PeerGroupType.None;

  clientProfiles: InstitutionProfile[] = [];
  clientProfile = signal<InstitutionProfile | undefined>(undefined);

  selectedInstitution?: InstitutionProfile;
  institutions: InstitutionProfile[] = [];
  removedInstituions: InstitutionProfile[] = [];
  distinctInstitutions: InstitutionProfile[] = [];
  distinctIids: number[] = [];
  selectionCriteria = '';

  date?: Date;
  states = GlobalConstants.states;
  selectedStates = [];
  ownershipTypes = [];
  typesOfMembership: TypeOfMembership[] = [];
  selectedTypesOfMembership = [];
  lowIncInd = false;

  rangeModified = false;

  assetRangeEnabled = true;
  assetRangeMask = 'separator';
  assetRangeMin?: number;
  assetRangeMax?: number;

  lnsToDepRangeEnabled = false;
  lnsToDepRangeMask = 'separator.2';
  lnsToDepRangeMin?: number;
  lnsToDepRangeMax?: number;

  assetGrowthRangeEnabled = false
  assetGrowthRangeMask = 'separator';
  assetGrowthRangeMin?: number;
  assetGrowthRangeMax?: number;

  resiInd = false;
  newAutoInd = false;
  usedAutoInd = false;
  creInd = false;
  firstTimeHomebuyer = false;
  creditBuilderRepairMain = false;
  mortgageSeller = false;
  leaseInd = false;
  studentInd = false;
  commlInd = false;
  ccInd = false;
  roeFilter = 0;
  roeMin?: number;
  roeMax?: number;
  roaFilter = 0;
  roaMin?: number;
  roaMax?: number;
  netIntMarginFilter = 0;
  netIntMarginMin?: number;
  netIntMarginMax?: number;
  netOverheadFilter = 0;
  netOverheadMin?: number;
  netOverheadMax?: number;
  yeaFilter = 0;
  yeaMin?: number;
  yeaMax?: number;
  yldOnLnsFilter = 0;
  yldOnLnsMin?: number;
  yldOnLnsMax?: number;
  cofFilter = 0;
  cofMin?: number;
  cofMax?: number;
  cstDepFilter = 0;
  cstDepMin?: number;
  cstDepMax?: number;
  texasRatioFilter = 0;
  texasRatioMin?: number;
  texasRatioMax?: number;
  memberGrowthFilter = 0;
  memberGrowthMin?: number;
  memberGrowthMax?: number;

  sortColumn = 'name';
  sortDir = 'asc';
  page = 1;

  isDirty = false;
  similarMore = false;
  basicPlan = false;
  loading = false;

  institutionProfileExpanded = false;
  loanConcentrationExpanded = false;
  servicesOfferedExpanded = false;
  performanceMetricsExpanded = false;

  @HostListener('window:beforeunload', ['$event'])
  onbeforeunload(event: { preventDefault: () => void; returnValue: boolean; }) {
    if (this.isDirty) {
      event.preventDefault();
      event.returnValue = false;
    }
  }

  ngOnInit() {
    this.profileService.getInstitutionProfiles(this.institution().iid).subscribe(profiles => {
      this.clientProfiles = profiles;
    });

    this.referenceService.getTypesOfMembership().subscribe(types => {
      this.typesOfMembership = types;
      this.typesOfMembership.sort(this.dynamicSort('tomCodeDesc', 'asc'));
    });
  }

  switchType(type: PeerGroupType) {
    if (this.selection == PeerGroupType.None) {
      this.selection = type;
      if (this.selection == PeerGroupType.Distinct) {
        this.institutions = this.distinctInstitutions;
        this.selectionCriteria = 'Distinct banks manually selected by ' + this.user()?.displayName;
      }
    }
  }

  onDateChange(date: Date) {
    this.date = date;
    const clientProfile = this.clientProfiles.find(i => new Date(i.reportDate).getTime() == date.getTime());
    if (clientProfile && !this.rangeModified) {
      this.assetRangeMin = Math.round((clientProfile.totalAssetsQe * .9) / 1000000);
      this.assetRangeMax = Math.round((clientProfile.totalAssetsQe * 1.1) / 1000000);
      this.lnsToDepRangeMin = Math.round(clientProfile.lnsToDep * .9);
      this.lnsToDepRangeMax = Math.round(clientProfile.lnsToDep * 1.1);
      this.assetGrowthRangeMin = 5;
      this.assetGrowthRangeMax = 10;
    }
    this.clientProfile.set(clientProfile);
    this.getInstitutions();
  }

  onStateChange() {
    this.getInstitutions();
  }

  onRangeChange() {
    this.rangeModified = true;
    this.getInstitutions();
  }
  
  getInstitutions() {
    this.page = 1;
    switch (this.selection) {
      case PeerGroupType.Similar: {
        if (this.date == undefined) return;
        const similarModel: SimilarInstitutionsRequest = {
          reportDate: this.date?.toISOString() ?? '',
          states: this.selectedStates,
          typesOfMembership: this.selectedTypesOfMembership
        }
        this.loading = true;
        this.profileService.getSimilarInstitutions(this.institution()!.iid, similarModel).subscribe(selection => {
          this.selectionCriteria = selection.selectionCriteria;
          this.institutions = selection.institutionProfiles.filter(i => this.removedInstituions.every(r => r.iid != i.iid));
          this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
          this.loading = false;
        });
        break;
      }
      case PeerGroupType.Targeted: {
        if (this.date == undefined) return;
        if (this.selectedStates.length == 0
          && this.lowIncInd == false
          && this.selectedTypesOfMembership.length == 0
          && ((this.assetRangeEnabled == false) || this.assetRangeMin == undefined && this.assetRangeMax == undefined)
          && ((this.lnsToDepRangeEnabled == false) || this.lnsToDepRangeMin == undefined && this.lnsToDepRangeMax == undefined)
          && ((this.assetGrowthRangeEnabled == false) || this.assetGrowthRangeMin == undefined && this.assetGrowthRangeMax == undefined)
          && this.resiInd == false && this.newAutoInd == false && this.usedAutoInd == false && this.creInd == false
          && this.firstTimeHomebuyer == false && this.creditBuilderRepairMain == false && this.mortgageSeller == false
          && this.leaseInd == false && this.studentInd == false && this.commlInd == false && this.ccInd == false
          && this.roeFilter == 0 && this.roaFilter == 0 && this.netIntMarginFilter == 0
          && this.netOverheadFilter == 0 && this.yeaFilter == 0 && this.yldOnLnsFilter == 0 && this.cofFilter == 0
          && this.cstDepFilter == 0 && this.texasRatioFilter == 0 && this.memberGrowthFilter == 0) {
          this.institutions = [];
          break;
        }
        const targetedModel: TargetedInstitutionsRequest = {
          reportDate: this.date?.toISOString() ?? '',
          states: this.selectedStates,
          typesOfMembership: this.selectedTypesOfMembership,
          lowIncInd: this.lowIncInd,
          useAssets: this.assetRangeEnabled,
          assetsMin: this.assetRangeMin ? this.assetRangeMin : undefined,
          assetsMax: this.assetRangeMax ? this.assetRangeMax : undefined,
          useLnsToDep: this.lnsToDepRangeEnabled,
          lnsToDepMin: this.lnsToDepRangeMin ? this.lnsToDepRangeMin : undefined,
          lnsToDepMax: this.lnsToDepRangeMax ? this.lnsToDepRangeMax : undefined,
          useAssetGrowth: this.assetGrowthRangeEnabled,
          assetGrowthMin: this.assetGrowthRangeMin ? this.assetGrowthRangeMin : undefined,
          assetGrowthMax: this.assetGrowthRangeMax ? this.assetGrowthRangeMax : undefined,
          resiInd: this.resiInd,
          newAutoInd: this.newAutoInd,
          usedAutoInd: this.usedAutoInd,
          creInd: this.creInd,
          firstTimeHomebuyer: this.firstTimeHomebuyer,
          creditBuilderRepairMain: this.creditBuilderRepairMain,
          mortgageSeller: this.mortgageSeller,
          leaseInd: this.leaseInd,
          studentInd: this.studentInd,
          commlInd: this.commlInd,
          ccInd: this.ccInd,
          roeFilter: this.roeFilter == -1 && !this.roeMin && !this.roeMax ? 0 : this.roeFilter,
          roeMin: this.roeMin != undefined && this.roeMin > 0 ? this.roeMin : undefined,
          roeMax: this.roeMax != undefined && this.roeMax > 0 ? this.roeMax : undefined,
          roaFilter: this.roaFilter == -1 && !this.roaMin && !this.roaMax ? 0 : this.roaFilter,
          roaMin: this.roaMin != undefined && this.roaMin > 0 ? this.roaMin : undefined,
          roaMax: this.roaMax != undefined && this.roaMax > 0 ? this.roaMax : undefined,
          netIntMarginFilter: this.netIntMarginFilter == -1 && !this.netIntMarginMin && !this.netIntMarginMax ? 0 : this.netIntMarginFilter,
          netIntMarginMin: this.netIntMarginMin != undefined && this.netIntMarginMin > 0 ? this.netIntMarginMin : undefined,
          netIntMarginMax: this.netIntMarginMax != undefined && this.netIntMarginMax > 0 ? this.netIntMarginMax : undefined,
          netOverheadFilter: this.netOverheadFilter == -1 && !this.netOverheadMin && !this.netOverheadMax ? 0 : this.netOverheadFilter,
          netOverheadMin: this.netOverheadMin != undefined && this.netOverheadMin > 0 ? this.netOverheadMin : undefined,
          netOverheadMax: this.netOverheadMax != undefined && this.netOverheadMax > 0 ? this.netOverheadMax : undefined,
          yeaFilter: this.yeaFilter == -1 && !this.yeaMin && !this.yeaMax ? 0 : this.yeaFilter,
          yeaMin: this.yeaMin != undefined && this.yeaMin > 0 ? this.yeaMin : undefined,
          yeaMax: this.yeaMax != undefined && this.yeaMax > 0 ? this.yeaMax : undefined,
          yldOnLnsFilter: this.yldOnLnsFilter == -1 && !this.yldOnLnsMin && !this.yldOnLnsMax ? 0 : this.yldOnLnsFilter,
          yldOnLnsMin: this.yldOnLnsMin != undefined && this.yldOnLnsMin > 0 ? this.yldOnLnsMin : undefined,
          yldOnLnsMax: this.yldOnLnsMax != undefined && this.yldOnLnsMax > 0 ? this.yldOnLnsMax : undefined,
          cofFilter: this.cofFilter == -1 && !this.cofMin && !this.cofMax ? 0 : this.cofFilter,
          cofMin: this.cofMin != undefined && this.cofMin > 0 ? this.cofMin : undefined,
          cofMax: this.cofMax != undefined && this.cofMax > 0 ? this.cofMax : undefined,
          cstDepFilter: this.cstDepFilter == -1 && !this.cstDepMin && !this.cstDepMax ? 0 : this.cstDepFilter,
          cstDepMin: this.cstDepMin != undefined && this.cstDepMin > 0 ? this.cstDepMin : undefined,
          cstDepMax: this.cstDepMax != undefined && this.cstDepMax > 0 ? this.cstDepMax : undefined,
          texasRatioFilter: this.texasRatioFilter == -1 && !this.texasRatioMin && !this.texasRatioMax ? 0 : this.texasRatioFilter,
          texasRatioMin: this.texasRatioMin != undefined && this.texasRatioMin > 0 ? this.texasRatioMin : undefined,
          texasRatioMax: this.texasRatioMax != undefined && this.texasRatioMax > 0 ? this.texasRatioMax : undefined,
          memberGrowthFilter: this.memberGrowthFilter == -1 && !this.memberGrowthMin && !this.memberGrowthMax ? 0 : this.memberGrowthFilter,
          memberGrowthMin: this.memberGrowthMin != undefined && this.memberGrowthMin > 0 ? this.memberGrowthMin : undefined,
          memberGrowthMax: this.memberGrowthMax != undefined && this.memberGrowthMax > 0 ? this.memberGrowthMax : undefined,
        }

        this.loading = true;
        this.profileService.getTargetedInstitutions(this.institution()!.iid, targetedModel).subscribe(selection => {
          this.selectionCriteria = selection.selectionCriteria;
          this.institutions = selection.institutionProfiles.filter(i => this.removedInstituions.every(r => r.iid != i.iid));
          this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
          this.loading = false;
        });
        break;
      }
      case PeerGroupType.Distinct: {
        this.institutions = this.distinctInstitutions;
        this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
        break;
      }
    }
  }

  onSelectInstitution(insitution: Institution) {
    this.profileService.getInstitutionProfile(insitution.iid).subscribe(profile => {
      this.selectedInstitution = profile;
      this.addInstitution();
    });
  }

  addInstitution() {
    this.institutionAutocompletes().forEach(autocomplete => autocomplete.clearInstitution());
    if (this.selectedInstitution) {
      this.selectedInstitution.isAdded = true;
      this.isDirty = true;
      this.distinctInstitutions.unshift(this.selectedInstitution);
      setTimeout(() => {
        if (this.selectedInstitution) {
          this.selectedInstitution.isAdded = false;
        }
      }, 3000);
      this.sortColumn = ''; this.sortDir = 'asc';
    }
  }

  removeInstitution(iid: number) {
    const institution = this.institutions.find(i => i.iid == iid) ?? {} as InstitutionProfile;
    institution.isRemoved = true;
    this.isDirty = true;
    setTimeout(() => {
      if (this.selection == PeerGroupType.Distinct) {
        institution.isRemoved = false;
        const index = this.distinctInstitutions.findIndex(i => i.iid == iid);
        this.distinctInstitutions.splice(index, 1);
      }
      else {
        institution.isRemoved = false;
        const index = this.institutions.findIndex(i => i.iid == iid);
        this.institutions.splice(index, 1);
        this.removedInstituions.push(institution);
      }
    }, 3000)
  }

  createGroup() {
    this.dialogService.show(this.dialog(), CreateGroupDialogComponent, { groupType: this.selection, numSelected: this.institutions.length })
      .subscribe((modalResult) => {
        if (modalResult.isSubmitted) {
          const results = modalResult.value as CreateGroupDialogResults;
          const group: CreateGroup = {
            name: results.name,
            description: results.description,
            criteria: this.selectionCriteria,
            iids: []
          }
          this.institutions.forEach(i => {
            group.iids.push(i.iid);
          });

          setTimeout(() => {
            this.saveGroup(group);
          }, 800);
        }
      });
    return false;
  }

  saveGroup(group: CreateGroup) {
    this.isDirty = false;
    this.stateService.createGroup(this.institution()!.iid, group as Group).subscribe(() => {
      this.router.navigate(['/peer-comparison/groups/manage']);
    });
  }

  discardChanges() {
    this.selectedStates = [];
    this.selectedTypesOfMembership = [];
    this.lowIncInd = false;
    this.assetRangeEnabled = true;
    this.lnsToDepRangeEnabled = false;
    this.assetGrowthRangeEnabled = false;
    this.resiInd = false;
    this.newAutoInd = false;
    this.usedAutoInd = false;
    this.creInd = false;
    this.firstTimeHomebuyer = false;
    this.creditBuilderRepairMain = false;
    this.mortgageSeller = false;
    this.leaseInd = false;
    this.studentInd = false;
    this.commlInd = false;
    this.ccInd = false;
    this.roeFilter = 0;
    this.roaFilter = 0;
    this.netIntMarginFilter = 0;
    this.netOverheadFilter = 0;
    this.yeaFilter = 0;
    this.yldOnLnsFilter = 0;
    this.cofFilter = 0;
    this.cstDepFilter = 0;
    this.texasRatioFilter = 0;
    this.memberGrowthFilter = 0;
    this.sortColumn = 'name';
    this.sortDir = 'asc';
    this.removedInstituions = [];
    this.distinctInstitutions = [];
    this.isDirty = false;
    this.selection = PeerGroupType.None;
    this.similarMore = false;
    this.selectionCriteria = '';
  }

  getSortClass(column: string): string {
    return this.sortColumn == column ? this.sortDir == 'asc' ? 'col-sort--ascend' : 'col-sort--decend' : '';
  }

  onSort(column: string) {
    if (this.sortColumn == column) {
      this.sortDir = this.sortDir == 'asc' ? 'desc' : 'asc';
    }
    else {
      this.sortColumn = column;
      this.sortDir = 'asc';
    }
    this.institutions.sort(this.dynamicSort(this.sortColumn, this.sortDir));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dynamicSort(property: any, dir: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (a: any, b: any) {
      const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
      return dir == 'asc' ? result : result * -1;
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    return !(this.isDirty && this.institutions.length > 0);
  }

  usdDisplay(value: number | undefined): string {
    return value != undefined ? new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value / 1000000) + 'MM' : '';
  }

  pctDisplay(value: number | undefined): string {
    return value != undefined ? value.toFixed(2) + '%' : '';
  }

  fixUrl(url: string): string {
    return !/^https?:\/\//i.test(url) ? 'http://' + url : url;
  }
}
